import React, { useEffect, useState } from "react";
import { Grid, Typography, Box, IconButton } from "@mui/material";
import { Table } from "react-bootstrap";
import { Dropdown } from "@fluentui/react/lib/Dropdown";
// Added by Gauri to fix dropdown position issue on 20 Nov 2025
import { DirectionalHint } from "@fluentui/react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AxiosInstance1 from "app/hooks/AxiosInstance/AxiosInstance1";

const HistorySectionR = ({ id }) => {
  const [historyData, setHistoryData] = useState([]);
  const [selectedField, setSelectedField] = useState(""); // Modified Field
  const [selectedBy, setSelectedBy] = useState(""); // Modified By
  // Added by Gauri to add pagination on 06 Nov 2025
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(5);
  const [loading, setLoading] = useState(false);
  // Updated by Gauri to bind dropdowns from list view API similar to ShowProgramHistory.jsx on 06 Nov 2025
  const [localDropdownOptions, setLocalDropdownOptions] = useState({
    modifiedfield_riskhistory: [{ key: "", text: "Select Modified Field" }],
    modifiedby_riskhistory: [{ key: "", text: "Select Modified By" }],
  });

  // Fetch all unique values for dropdowns without filters - Updated by Gauri on 06 Nov 2025
  useEffect(() => {
    const fetchAllUniqueValues = async () => {
      if (!id) return;

      try {
        // Fetch all records with large page size to get all unique values
        const url = `/api/InitiativeRisk/GetInitiativeRisksHistory?RiskID=${id}&PageNumber=1&PageSize=1000`;

        const response = await AxiosInstance1.get(url, {
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (response.status !== 200) {
          throw new Error("Failed to fetch data");
        }

        const data = response?.data;
        const allData = data?.data?.listInitiativeRiskhistory || [];

        // Extract all unique field names
        const uniqueFields = [...new Set(allData.map(item => item.fieldName).filter(Boolean))];
        
        // Extract all unique modified by names
        const uniqueModifiedBy = [...new Set(allData.map(item => item.modifiedBy).filter(Boolean))];

        // Update dropdown options with all unique values
        setLocalDropdownOptions({
          modifiedfield_riskhistory: [
            { key: "", text: "Select Modified Field" },
            ...uniqueFields.map(field => ({ key: field, text: field }))
          ],
          modifiedby_riskhistory: [
            { key: "", text: "Select Modified By" },
            ...uniqueModifiedBy.map(name => ({ key: name, text: name }))
          ],
        });
      } catch (error) {
        console.error("Error fetching unique values:", error);
      }
    };

    fetchAllUniqueValues();
  }, [id]);

  useEffect(() => {
    const fetchTableData = async () => {
      if (!id) return;

      setLoading(true);
      const token = sessionStorage.getItem("access_token");
      // Added by Gauri to add pagination parameters on 06 Nov 2025
      const url = `/api/InitiativeRisk/GetInitiativeRisksHistory?RiskID=${id}&FiledName=${selectedField}&ModifiedBy=${selectedBy}&PageNumber=${currentPage}&PageSize=${pageSize}`;

      try {
        const response = await AxiosInstance1.get(url, {
          // method: "GET",
          headers: {
            // Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (response.status !== 200) {
          throw new Error(`Failed to fetch data: ${response.statusText}`);
        }

        const data = response?.data;
        // Added by Gauri to extract pagination info from listPagination on 06 Nov 2025
        const totalRecords = data?.data?.listPagination?.[0]?.totalRecords || 0;
        setHistoryData(data.data.listInitiativeRiskhistory || []);
        setTotalCount(totalRecords);
      } catch (error) {
        console.error("Error fetching table data:", error);
        setHistoryData([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    };

    fetchTableData();
  }, [selectedField, selectedBy, id, currentPage, pageSize]);

  // Added by Gauri to handle pagination on 06 Nov 2025
  const handlePreviousPage = () => {
    setCurrentPage((prevPage) => Math.max(prevPage - 1, 1));
  };

  const handleNextPage = () => {
    const maxPages = Math.ceil(totalCount / pageSize);
    setCurrentPage((prevPage) => {
      if (prevPage >= maxPages) {
        return prevPage;
      }
      return prevPage + 1;
    });
  };

  // Added by Gauri to format dates as DD/MM/YYYY on 06 Nov 2025
  const formatDate = (dateValue) => {
    if (!dateValue) return "";
    
    try {
      const dateObj = new Date(dateValue);
      if (!isNaN(dateObj.getTime())) {
        // Format as DD/MM/YYYY
        const day = String(dateObj.getDate()).padStart(2, '0');
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const year = dateObj.getFullYear();
        return `${day}/${month}/${year}`;
      }
    } catch (error) {
      console.error("Error formatting date:", error);
    }
    
    return "";
  };

  const handleFieldChange = (_, option) => {
    setSelectedField(option?.key || "");
    // Removed clearing Modified By filter - filters should work independently
    // Updated by Gauri on 06 Nov 2025
    // Added by Gauri to reset page to 1 when filter changes on 06 Nov 2025
    setCurrentPage(1);
  };

  const handleByChange = (_, option) => {
    setSelectedBy(option?.key || "");
    // Added by Gauri to reset page to 1 when filter changes on 06 Nov 2025
    setCurrentPage(1);
  };

  // Added by Gauri on 20 Nov 2025 - Common calloutProps for all dropdowns
  const commonCalloutProps = {
    // Use bottomAutoEdge which tries to open downward first, then automatically adjusts upward if needed
    directionalHint: DirectionalHint.bottomAutoEdge,
    // Set max height to prevent dropdown from being too large
    calloutMaxHeight: 250,
    // Ensure dropdown stays within viewport bounds
    bounds: typeof window !== 'undefined' ? {
      top: 0,
      left: 0,
      bottom: window.innerHeight,
      right: window.innerWidth
    } : undefined
  };

  return (
    <div>
      <Grid container spacing={2} alignItems="center" sx={{ marginBottom: 2 }}>
        <Grid item xs={6}>
          <Typography variant="subtitle1" sx={{ fontSize: "0.775rem" }}>
            Modified Field
          </Typography>
          {/* Modified by Gauri to fix dropdown position issue on 20 Nov 2025 */}
          <Dropdown
            placeholder="Select Modified Field"
            options={localDropdownOptions.modifiedfield_riskhistory}
            selectedKey={selectedField || null}
            onChange={handleFieldChange}
            styles={{ dropdown: { width: 200 } }}
            calloutProps={commonCalloutProps}
          />
        </Grid>
        <Grid item xs={6}>
          <Typography variant="subtitle1" sx={{ fontSize: "0.775rem" }}>
            Modified By
          </Typography>
          {/* Modified by Gauri to fix dropdown position issue on 20 Nov 2025 */}
          <Dropdown
            placeholder="Select Modified By"
            options={localDropdownOptions.modifiedby_riskhistory}
            selectedKey={selectedBy || ""}
            onChange={handleByChange}
            styles={{ dropdown: { width: 200 } }}
            calloutProps={commonCalloutProps}
          />
        </Grid>
      </Grid>
      <Table striped bordered hover>
        <thead>
          <tr>
            <th>Modified Field</th>
            <th>Modified Date</th>
            <th>Value</th>
            <th>Modified By</th>
          </tr>
        </thead>
        <tbody>
          {historyData.length > 0 ? (
            historyData.map((row, index) => (
              <tr key={index}>
                <td>{row.fieldName || "N/A"}</td>
                {/* Added by Gauri to format dates as DD/MM/YYYY on 06 Nov 2025 */}
                <td>{formatDate(row.modifiedDate)}</td>
                <td>{row.value || "N/A"}</td>
                <td>{row.modifiedBy || "N/A"}</td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="4" style={{ textAlign: "center" }}>
                There are no items to show in this view.
              </td>
            </tr>
          )}
        </tbody>
      </Table>
      {/* Added by Gauri to add pagination controls on 06 Nov 2025 */}
      <Box sx={{ display: "flex", justifyContent: "center", marginTop: "20px" }}>
        <IconButton onClick={handlePreviousPage} disabled={currentPage === 1 || totalCount === 0 || loading}>
          <ArrowBackIcon />
        </IconButton>
        <Typography sx={{ margin: "0 10px", alignSelf: "center" }}>
          Page {currentPage}
        </Typography>
        <IconButton
          onClick={handleNextPage}
          disabled={totalCount === 0 || currentPage >= Math.ceil(totalCount / pageSize) || loading}
        >
          <ArrowForwardIcon />
        </IconButton>
      </Box>
    </div>
  );
};

export default HistorySectionR;

